import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { SortAccessors } from '@/lib/tableSort'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import { ColumnCells, ResizableTh } from '@/components/table'
import { GripVertical, History, Loader2, Pencil, Plus, Sparkles, Trash2, X } from 'lucide-react'
import { StorageAuditPanel } from '@/components/inventory/storage/StorageAuditPanel'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  useDeleteZone,
  useSaveZone,
  useSeedDefaultZones,
  useStorageZones,
} from '@/hooks/usePickLists'
import { PERMISSIONS as P, usePermission } from '@/lib/permissions'
import type { CreateZonePayload, StorageZone } from '@/types/pickList'

const BLANK: CreateZonePayload = {
  code: '',
  name: '',
  tempRange: '',
  handlingNote: '',
  routeOrder: 0,
  active: true,
}

/**
 * Storage zones — the physical areas a picker walks between.
 *
 * `routeOrder` is the load-bearing field: a pick sheet is grouped and ordered
 * by it, so it defines the route someone actually walks. Coldest last keeps
 * material out of temperature for the shortest time.
 */
// Per-user column layout. Code and Name are locked (together they identify the
// zone) and so is the actions cell. See src/components/table/index.ts.
const ZONES_TABLE_ID = 'storage-zones'

const ZONES_COLUMNS: TableColumnDef[] = [
  { id: 'grip', header: 'Reorder', locked: true },
  { id: 'code', header: 'Code', locked: true, sortable: true },
  { id: 'name', header: 'Name', locked: true, sortable: true },
  { id: 'temperature', header: 'Temperature', sortable: true },
  { id: 'handlingNote', header: 'Handling note', sortable: true },
  { id: 'route', header: 'Route', sortable: true },
  { id: 'active', header: 'Active', sortable: true },
  { id: 'actions', header: 'Actions', locked: true },
]

// Header sorting (z8nrz7e4h6). Unsorted, zones stay in route order (the API's
// order); a sort only changes the view — the route itself lives in routeOrder,
// which the Route column shows and sorts by. The grip is a static glyph.
const ZONES_SORT: SortAccessors<StorageZone> = {
  code: z => z.code,
  name: z => z.name,
  temperature: z => z.tempRange,
  handlingNote: z => z.handlingNote,
  route: z => z.routeOrder,
  active: z => z.active,
}

const ZONE_HEAD_CLASSES: Record<string, string> = {
  grip: 'w-10 px-3 py-2.5',
  code: 'w-20 px-3 py-2.5 text-left font-semibold',
  name: 'px-3 py-2.5 text-left font-semibold',
  temperature: 'w-28 px-3 py-2.5 text-left font-semibold',
  handlingNote: 'px-3 py-2.5 text-left font-semibold',
  route: 'w-20 px-3 py-2.5 text-right font-semibold',
  active: 'w-20 px-3 py-2.5 text-left font-semibold',
  actions: 'w-20 px-3 py-2.5',
}

export function StorageZonesConfig() {
  const cols = useTableColumns(ZONES_TABLE_ID, ZONES_COLUMNS)
  const { data: zones = [], isLoading } = useStorageZones()
  const sortedZones = useSortedRows(zones, cols.sort, ZONES_SORT)
  const save = useSaveZone()
  const remove = useDeleteZone()
  const seed = useSeedDefaultZones()
  const { can } = usePermission()
  const canConfigure = can(P.inventoryConfigure)

  const [editingId, setEditingId] = useState<string | null>(null)
  // One zone's trail open at a time. It renders as a full-width row under the
  // table, so two at once would push the second well off the screen.
  const [historyFor, setHistoryFor] = useState<string | null>(null)
  const [draft, setDraft] = useState<CreateZonePayload>(BLANK)
  const [error, setError] = useState<string | null>(null)

  const startCreate = () => {
    setEditingId('new')
    setDraft({ ...BLANK, routeOrder: zones.length + 1 })
    setError(null)
  }

  const startEdit = (z: StorageZone) => {
    setEditingId(z.id)
    setDraft({
      code: z.code,
      name: z.name,
      tempRange: z.tempRange ?? '',
      handlingNote: z.handlingNote ?? '',
      routeOrder: z.routeOrder,
      active: z.active,
    })
    setError(null)
  }

  const cancel = () => {
    setEditingId(null)
    setError(null)
  }

  const commit = () => {
    if (!draft.code.trim() || !draft.name.trim()) {
      setError('Code and name are both required.')
      return
    }
    save.mutate(
      { id: editingId === 'new' ? undefined : (editingId ?? undefined), payload: draft },
      {
        onSuccess: () => setEditingId(null),
        onError: e => setError(e instanceof Error ? e.message : 'Could not save the zone.'),
      },
    )
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h3 className='text-sm font-semibold'>Storage Zones</h3>
          <p className='mt-0.5 max-w-2xl text-xs text-muted-foreground'>
            The physical areas stock is kept in. Pick sheets group lines by zone and walk them in
            route order, so keep the order matching the path someone actually takes — ambient
            first, coldest last.
          </p>
        </div>
        {canConfigure && (
          <div className='flex shrink-0 gap-2'>
            {zones.length === 0 && (
              <Button
                size='sm'
                variant='outline'
                className='gap-1.5'
                disabled={seed.isPending}
                onClick={() => seed.mutate()}
              >
                {seed.isPending ? (
                  <Loader2 className='h-3.5 w-3.5 animate-spin' />
                ) : (
                  <Sparkles className='h-3.5 w-3.5' />
                )}
                Create default zones
              </Button>
            )}
            <Button size='sm' className='gap-1.5' onClick={startCreate}>
              <Plus className='h-3.5 w-3.5' />
              New zone
            </Button>
          </div>
        )}
      </div>

      {isLoading ? (
        <div className='flex items-center gap-2 p-6 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading zones…
        </div>
      ) : (
        <>
        <div className='overflow-hidden rounded-xl border bg-card'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='border-b bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground'>
                {cols.visible.map(({ def }) => (
                  <ResizableTh
                    key={def.id}
                    columnId={def.id}
                    columns={cols}
                    label={def.header}
                    className={ZONE_HEAD_CLASSES[def.id]}
                    resizable={def.id !== 'grip' && def.id !== 'actions'}
                  >
                    {def.id === 'grip' ? null : def.header}
                  </ResizableTh>
                ))}
              </tr>
            </thead>
            <tbody>
              {zones.length === 0 && editingId !== 'new' && (
                <tr>
                  <td colSpan={cols.visible.length} className='px-3 py-10 text-center text-sm text-muted-foreground'>
                    No storage zones yet. Until one exists, every pick-sheet line falls into an
                    “unassigned” group with no location.
                  </td>
                </tr>
              )}

              {sortedZones.map(z =>
                editingId === z.id ? (
                  <EditRow
                    key={z.id}
                    draft={draft}
                    setDraft={setDraft}
                    onCancel={cancel}
                    onSave={commit}
                    saving={save.isPending}
                  />
                ) : (
                  <tr key={z.id} className='border-b last:border-0 hover:bg-muted/30'>
                    <ColumnCells
                      columns={cols}
                      cells={{
                        grip: (
                          <td className='px-3 py-2 text-muted-foreground'>
                            <GripVertical className='h-3.5 w-3.5' />
                          </td>
                        ),
                        code: (
                          <td className='px-3 py-2'>
                            <span className='rounded bg-sky-50 px-1.5 py-0.5 font-mono text-xs font-bold text-sky-800'>
                              {z.code}
                            </span>
                          </td>
                        ),
                        name: <td className='px-3 py-2 font-medium'>{z.name}</td>,
                        temperature: (
                          <td className='px-3 py-2'>
                            {z.tempRange ? (
                              <span className='rounded-full border px-2 py-0.5 text-[11px] font-semibold text-sky-800'>
                                {z.tempRange}
                              </span>
                            ) : (
                              <span className='text-muted-foreground'>—</span>
                            )}
                          </td>
                        ),
                        handlingNote: (
                          <td className='px-3 py-2 text-xs italic text-muted-foreground'>
                            {z.handlingNote || '—'}
                          </td>
                        ),
                        route: <td className='px-3 py-2 text-right tabular-nums'>{z.routeOrder}</td>,
                        active: (
                          <td className='px-3 py-2'>
                            <span
                              className={cn(
                                'inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold',
                                z.active
                                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                  : 'border-slate-200 bg-slate-50 text-slate-500',
                              )}
                            >
                              {z.active ? 'ACTIVE' : 'OFF'}
                            </span>
                          </td>
                        ),
                        actions: (
                          <td className='px-3 py-2'>
                            <div className='flex justify-end gap-0.5'>
                              {/* Reading the trail is inventory.view, not configure —
                                  being able to see who changed a zone is not the same
                                  permission as being able to change one. */}
                              <Button
                                size='icon'
                                variant='ghost'
                                className='h-7 w-7'
                                title='History — who changed this zone, and when'
                                onClick={() =>
                                  setHistoryFor((prev) => (prev === z.id ? null : z.id))
                                }
                              >
                                <History className='h-3.5 w-3.5' />
                              </Button>
                              {canConfigure && (
                                <>
                                  <Button
                                    size='icon'
                                    variant='ghost'
                                    className='h-7 w-7'
                                    title='Edit zone'
                                    onClick={() => startEdit(z)}
                                  >
                                    <Pencil className='h-3.5 w-3.5' />
                                  </Button>
                                  <Button
                                    size='icon'
                                    variant='ghost'
                                    className='h-7 w-7 text-destructive hover:text-destructive'
                                    title='Delete zone — its lots stay, but lose their location'
                                    disabled={remove.isPending}
                                    onClick={() => remove.mutate(z.id)}
                                  >
                                    <Trash2 className='h-3.5 w-3.5' />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        ),
                      }}
                    />
                  </tr>
                ),
              )}

              {/* Rendered as its own full-width row rather than inside the
                  actions cell: a trail is a list, and nesting one in a table
                  cell squeezes it into the narrowest column on the screen. */}
              {historyFor && (
                <tr className='border-b bg-muted/20 last:border-0'>
                  <td colSpan={cols.visible.length} className='px-3 py-2'>
                    <StorageAuditPanel
                      key={historyFor}
                      zoneId={historyFor}
                      title={`History of ${zones.find((z) => z.id === historyFor)?.code ?? 'zone'}`}
                      defaultOpen
                    />
                  </td>
                </tr>
              )}

              {editingId === 'new' && (
                <EditRow
                  draft={draft}
                  setDraft={setDraft}
                  onCancel={cancel}
                  onSave={commit}
                  saving={save.isPending}
                />
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {error && <p className='text-xs text-destructive'>{error}</p>}
    </div>
  )
}

function EditRow({
  draft,
  setDraft,
  onCancel,
  onSave,
  saving,
}: {
  draft: CreateZonePayload
  setDraft: (d: CreateZonePayload) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
}) {
  return (
    <tr className='border-b bg-primary/5 last:border-0'>
      <td className='px-3 py-2' />
      <td className='px-2 py-2'>
        <Input
          value={draft.code}
          onChange={e => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
          placeholder='Z1'
          className='h-8 font-mono text-xs'
        />
      </td>
      <td className='px-2 py-2'>
        <Input
          value={draft.name}
          onChange={e => setDraft({ ...draft, name: e.target.value })}
          placeholder='AMBIENT STORE'
          className='h-8 text-xs'
        />
      </td>
      <td className='px-2 py-2'>
        <Input
          value={draft.tempRange ?? ''}
          onChange={e => setDraft({ ...draft, tempRange: e.target.value })}
          placeholder='15 to 25 C'
          className='h-8 text-xs'
        />
      </td>
      <td className='px-2 py-2'>
        <Input
          value={draft.handlingNote ?? ''}
          onChange={e => setDraft({ ...draft, handlingNote: e.target.value })}
          placeholder='Return to store within 30 min if not consumed.'
          className='h-8 text-xs'
        />
      </td>
      <td className='px-2 py-2'>
        <Input
          type='number'
          min={0}
          value={draft.routeOrder ?? 0}
          onChange={e => setDraft({ ...draft, routeOrder: Number(e.target.value) })}
          className='h-8 text-right text-xs'
        />
      </td>
      <td className='px-2 py-2'>
        <div className='flex items-center gap-2'>
          <Switch
            id='zone-active'
            checked={draft.active ?? true}
            onCheckedChange={v => setDraft({ ...draft, active: v })}
          />
          <Label htmlFor='zone-active' className='sr-only'>
            Active
          </Label>
        </div>
      </td>
      <td className='px-2 py-2'>
        <div className='flex justify-end gap-0.5'>
          <Button size='sm' className='h-7 px-2 text-xs' disabled={saving} onClick={onSave}>
            {saving ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : 'Save'}
          </Button>
          <Button
            size='icon'
            variant='ghost'
            className='h-7 w-7'
            title='Cancel'
            onClick={onCancel}
          >
            <X className='h-3.5 w-3.5' />
          </Button>
        </div>
      </td>
    </tr>
  )
}
